import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useCockpitStore } from '../../store/cockpitStore';
import {
  PeriodSelect, ActivityPulse, PipelineConversions, CustomerHealth, HygienePanel, QuickAccess,
} from '../../components/cockpit';
import { Users2, ArrowRight, AlertCircle } from 'lucide-react';

/**
 * StatsView — the rep's performance review (self-scoped "mine"), under the
 * "Performance" tab. Reprioritized: the numbers reps check first lead, the old
 * "recently viewed" columns are replaced by actionable Quick access (favorites +
 * lists). Managers also get a "View team →" link.
 */
export const StatsView = () => {
  const { user, canViewAllPeople } = useAuthStore();
  const { data, period, isLoading, error, fetch } = useCockpitStore();

  useEffect(() => {
    fetch('mine', period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Header: title + compact period picker + (managers) team link */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Performance</h1>
        <div className="flex items-center gap-2 sm:gap-3">
          <PeriodSelect value={period} onChange={(p) => fetch('mine', p)} />
          {canViewAllPeople() && (
            <Link
              to="/team"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-blast-700 dark:text-blast-300 bg-blast-50 dark:bg-blast-950 hover:bg-blast-100 whitespace-nowrap"
            >
              <Users2 className="w-4 h-4" /> <span className="hidden sm:inline">View team</span> <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>

      {/* Quick access — favorites + saved lists, up top for fast jumps */}
      <div className="border-b border-gray-200 dark:border-gray-800 pb-6">
        <QuickAccess />
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
      {isLoading && !data && <div className="card p-6 text-sm text-gray-400 dark:text-gray-500">Loading…</div>}

      {/* Gate on scope so a stale team-scope payload (from /team in the shared store)
          never flashes under the self view before the mine-scope refetch lands. */}
      {data && data.scope === 'mine' && (
        <>
          <ActivityPulse data={data.activityPulse} />
          <PipelineConversions pipeline={data.pipeline} temperature={data.temperature} conversions={data.conversions} scope="mine" />
          <CustomerHealth data={data.customerHealth} />
          <HygienePanel data={data.hygiene} />
        </>
      )}
    </div>
  );
};
